<template>
    <font-awesome-icon v-if="isLoading" icon="spinner" spin/>
    <div v-else>
        <b-row>
            <b-col cols="12" md="9" lg="7">
                <b-card class="mb-4 d-none d-md-block">
                    <b-card-title>METADATA</b-card-title>

                    <b-table-simple small>
                        <colgroup>
                            <col style="width:250px;"/>
                            <col/>
                        </colgroup>
                        <tbody>
                        <tr>
                            <b-th variant="light">Aangemaakt op:</b-th>
                            <td>{{ batch.created_at | datetime }}</td>
                        </tr>
                        <tr>
                            <b-th variant="light">Type:</b-th>
                            <td>{{ batch.type.display_name }}</td>
                        </tr>
                        <tr>
                            <b-th variant="light">Status:</b-th>
                            <td v-if="isStatusEditable" class="py-0">
                                <b-select v-model="batch.status" size="sm" style="margin-top:2px;"
                                          :options="selectableStatuses"
                                          @input="saveBatch"
                                />
                            </td>
                            <td v-else>{{ dispatchRequestBatchStatuses[batch.status] }}</td>
                        </tr>
                        <tr>
                            <b-th variant="light">Dispatcher:</b-th>
                            <td>
                                <location-link :location="batch.dispatcher"/>
                            </td>
                        </tr>
                        <tr>
                            <b-th variant="light">Dispatch-datum:</b-th>
                            <td><span v-if="batch.dispatch_date !== '0000-00-00'">{{ batch.dispatch_date | date }}</span></td>
                        </tr>
                        <tr v-if="isSortable">
                            <b-th variant="light">Sorteerzone:</b-th>
                            <td>
                                {{currentSortingZone.display_name}}

                                <b-button v-if="!currentSortingZone.id && hasPermission('orders.batch.process.sort') && isFeatureEnabled('batching')" variant="link" @click="showSelectedSortingZoneModal = !showSelectedSortingZoneModal" class="p-0">
                                    <span >Maak een keuze</span>
                                </b-button>

                                <b-modal v-model="showSelectedSortingZoneModal" title="Kies sorteerzone"
                                         centered dialog-class="shadow-none"
                                         @ok="saveSortingZone"
                                >
                                    <b-select v-model="selectedSortingZone"
                                              :options="sortingZones" text-field="display_name" value-field="identifier"
                                    />
                                    <template #modal-footer="{cancel}">
                                        <b-button variant="secondary" @click="cancel">Annuleren</b-button>
                                        <b-button variant="success" @click="saveSortingZone" :disabled="!selectedSortingZone || isSaving">
                                            <span v-if="!isSaving">Opslaan</span>
                                            <font-awesome-icon v-else icon="spinner" spin/>
                                        </b-button>
                                    </template>
                                </b-modal>
                            </td>
                        </tr>
                        <tr v-if="isSortable">
                            <b-th variant="light">Picklijst</b-th>
                            <td v-if="!currentSortingZone.id && isFeatureEnabled('batching')"><span class="text-secondary">Kies eerst een sorteerzone</span></td>
                            <td v-else>
                                <b-button variant="link" @click="showGeneratePickListModal = !showGeneratePickListModal" class="p-0">
                                    Genereer picklijst
                                </b-button>

                                <b-modal v-model="showGeneratePickListModal" title="Genereer picklijst"
                                         centered dialog-class="shadow-none"
                                >
                                    <b-table-simple>
                                        <colgroup>
                                            <col style="width:200px;"/>
                                            <col/>
                                        </colgroup>
                                        <b-tbody>
                                            <b-tr>
                                                <b-th variant="light">Sorteervolgorde</b-th>
                                                <b-td>
                                                    <b-select v-model="picklistForm.sortOrder" :options="PICKLIST_SORT_ORDERS"/>
                                                </b-td>
                                            </b-tr>
                                            <b-tr>
                                                <b-th variant="light">Bestandsformaat</b-th>
                                                <b-td>
                                                    <b-select v-model="picklistForm.contentType" :options="PICKLIST_CONTENT_TYPE"/>
                                                </b-td>
                                            </b-tr>
                                        </b-tbody>
                                    </b-table-simple>

                                    <template #modal-footer="{cancel}">
                                        <b-button variant="secondary" @click="cancel">Annuleren</b-button>
                                        <b-button variant="success" @click="generatePickList" :disabled="isSaving">
                                            <span v-if="!isSaving">Genereer</span>
                                            <font-awesome-icon v-else icon="spinner" spin/>
                                        </b-button>
                                    </template>
                                </b-modal>
                            </td>
                        </tr>
                        </tbody>
                    </b-table-simple>
                </b-card>

            </b-col>

            <b-col cols="12" md="3" lg="5" class="text-right">
                <b-button v-if="!isSortable && (batch.status === 'created' || isSaving)"
                          variant="outline-secondary"
                          @click="closeBatch"
                          class="mb-4 mr-2"
                >
                    Batch sluiten
                </b-button>

                <b-button v-if="isSortable && currentSortingZone.id && batch.status !== 'closed' && batch.status !== 'shipped'"
                          :to="getScanRoute('sort')"
                          class="bg-midnight-bloom text-white mb-4"
                >
                    Start met scannen
                </b-button>
            </b-col>
        </b-row>

        <b-card>
            <b-card-title>ORDERS</b-card-title>
            <order-list :columns="orderListColumns" :filters="orderListFilters"/>
        </b-card>
    </div>
</template>

<script>
import OrderList from "../../components/OrderList.vue";
import orderFilterSet from "../../models/OrderFilterSet";
import PageTitle from "../../components/PageTitle.vue";
import orderListColumns from "../../components/OrderListColumns";
import LocationLink from "../../components/LocationLink.vue";
import {BATCH_MODEL_TYPE_SHIPMENT_BATCH} from "../../data/batchModelTypes";
import _ from "lodash"

export default {
    name: "ShipmentBatchView",
    components: {LocationLink, PageTitle, OrderList},

    props: {
        location: {required: false},
        batchIdentifier: {required: true, type: String},
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            showSelectedSortingZoneModal: false,
            showGeneratePickListModal: false,
            batch: {},
            sortingZones: [],
            selectedSortingZone: '',
            picklistForm: {
                sortOrder: 'product_name',
                contentType: 'application/pdf',
            },
            PICKLIST_SORT_ORDERS: {
                'product_name': 'Productnaam',
                'product_quantity': 'Aantal',
            },
            PICKLIST_CONTENT_TYPE: {
                'application/pdf': 'PDF',
                'text/html': 'HTML',
            },
        }
    },

    computed: {
        isSortable() {
            return this.batch.type.is_batchable
        },
        isStatusEditable() {
            return this.isFeatureEnabled('batching') && Object.keys(this.selectableStatuses).length && this.batch.type.batch_configuration.is_batch_status_editable
        },
        orderListColumns() {
            let columns = orderListColumns.getColumnsWithActiveState(
                'orderIdentifier',
                'placed_at',
                'dispatchDate',
                'drqStatus',
                'productCount',
            )

            columns.push({
                identifier: 'sorting_location',
                name: 'Legplaats',
                property: 'dispatch_requests',
                postProcess: (drqs) => {
                    try {
                        return drqs
                            .filter(drq => drq.status === 'requested')
                            .map(drq => `${drq.storage_location.storage_zone.display_name} &rarr; ${drq.storage_location.display_name}`)
                            .join(`<br/>`)
                    } catch (e) {
                        return ''
                    }
                },
                is_active: true,
            })

            return columns
        },
        orderListFilters() {
            return {
                ...orderFilterSet,
                dispatch_request_batch: this.batchIdentifier,
                dispatcher_type: this.batch.dispatcher_type,
                dispatcher_identifier: this.batch.dispatcher_identifier,
                include: 'dispatch_requests.storage_location.storage_zone',
            }
        },
        currentSortingZone() {
            const zone = this.sortingZones.find(z => z.id === this.batch.storage_zone_id)
            return zone ? zone : {}
        },
        selectableStatuses() {
            return _.pickBy(this.dispatchRequestBatchStatuses, (name, identifier) => this.batch.allowed_status_changes.indexOf(identifier) !== -1)
        },
    },

    mounted() {
        Promise.all([
            this.$store.getters.getLocationDataTypes,
            this.loadBatch(),
            this.loadSortingZones(),
        ])
            .then(() => {
                this.isLoading = false
            })
    },

    methods: {
        async loadBatch() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/dispatch-request-batches/${this.batchIdentifier}?include=type,dispatcher`)
                .then(response => {
                    this.batch = response.data
                })
                .catch(e => {
                    this.$alert(`Batch niet gevonden`, 'danger')
                })
        },
        async loadSortingZones() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/storage-locations?type=sort`)
                .then(response => {
                    this.sortingZones = response.data
                })
        },
        async saveSortingZone() {
            this.isSaving = true
            return this.$http.patch(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/dispatch-request-batches/${this.batch.identifier}`, {
                sorting_zone_identifier: this.selectedSortingZone,
            })
                .then(response => {
                    this.batch.storage_zone_id = this.sortingZones.find(z => z.identifier === this.selectedSortingZone).id
                    this.showSelectedSortingZoneModal = false
                })
                .catch(e => {
                    if (e.response.data.errors[0].identifier === 'ZoneFullyOccupied') {
                        return this.$alert("Er zijn te veel onvoltooide batches in deze zone. Zet de status van deze batches op 'Verzonden' of 'Kan niet verzenden' om dit op te lossen.", 'warning')
                    }

                    this.$alert('Fout bij het opslaan van de sorteerzone', 'danger')
                })
                .then(() => this.isSaving = false)
        },
        async generatePickList() {
            this.isSaving = true
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/dispatch-request-batches/${this.batch.identifier}/pick-list`, {
                responseType: 'blob',
                params: {
                    sort_order: this.picklistForm.sortOrder,
                },
                headers: {
                    Accept: this.picklistForm.contentType,
                },
            })
                .then(response => {
                    let reader = new FileReader()
                    reader.readAsDataURL(response.data)
                    reader.onload = (e) => {
                        let a = document.createElement('a')
                        a.href = e.target.result
                        a.download = `picklist-${this.batch.identifier}.${this.picklistForm.contentType.split('/')[1]}`
                        document.body.appendChild(a)
                        a.click()
                        document.body.removeChild(a)
                    }
                    this.showGeneratePickListModal = false;
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het genereren van de picklist', 'danger')
                })
                .then(() => this.isSaving = false)
        },
        async saveBatch() {
            this.isSaving = true
            return this.$http.patch(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/dispatch-request-batches/${this.batchIdentifier}`, {
                status: this.batch.status,
            })
                .then(response => {
                    this.$alert(`Opgeslagen`)
                })
                .catch(e => {
                    this.$alert('Fout bij het opslaan', 'danger')
                    this.loadBatch()
                })
                .then(() => {
                    this.isSaving = false
                })

        },
        closeBatch() {
            this.batch.status = 'closed'
            this.saveBatch()
        },
        getScanRoute(processType) {
            return {
                name: 'ProcessStartWithBatch',
                params: {
                    locationTypeIdentifier: this.location.location_type.identifier,
                    locationIdentifier: this.location.identifier,
                    batchModelType: BATCH_MODEL_TYPE_SHIPMENT_BATCH,
                    batchModelIdentifier: this.batchIdentifier,
                    processType: processType,
                },
            }
        },
    },
}
</script>
